import React from 'react';
import GenericDialogue from 'src/components/Dialogue/Dialogue';
import Skeleton from 'src/components/Skeleton/Skeleton';
import { EventType } from 'src/utils/BigCalendarColourGeneratorUtils';
import {
  ICalendarEvent,
  ICalendarEventData,
  KEY_PICKUP_EVENT_TYPE,
  SERVICE_REQUEST_EVENT_TYPE,
  WALKTHROUGH_EVENT_TYPE,
} from 'src/pages/Calendar/types';
import CalcomEventDetailsSection from 'src/pages/Calendar/CalcomIntegration/components/CalcomEventDetails/CalcomEventDetails';
import ServiceRequestSchedulingNote from 'src/pages/Calendar/CalcomIntegration/components/ServiceRequestSchedulingNote/ServiceRequestSchedulingNote';
import { resolveEventTypeLabel, resolveEventTypeStyleKey } from './helpers';
import EventDetailsHeader from './components/EventDetailsHeader/EventDetailsHeader';
import KeyPickupEventInformation from './components/EventInformation/KeyPickupEventInformation';
import KeyPickupDetails from './components/KeyPickupDetails/KeyPickupDetails';
import KeyPickupHandedOffCard from './components/KeyPickupHandedOffCard/KeyPickupHandedOffCard';
import {
  KeyPickupHandoffStatus,
  useKeyPickupHandoffStatus,
} from './useKeyPickupHandoffStatus';
import ServiceRequestDetails from './components/ServiceRequestDetails/ServiceRequestDetails';
import MobileAppCtaCard from './components/MobileAppCtaCard/MobileAppCtaCard';
import EventCreatedAt from './components/EventCreatedAt/EventCreatedAt';
import './event-details-dialog.scss';

interface IEventDetailsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  event: ICalendarEvent;
}

const EventDetailsDialog = ({
  isOpen,
  onClose,
  event,
}: IEventDetailsDialogProps) => {
  const originalEvent: ICalendarEventData | null = event?.originalEvent ?? null;

  const { isHandedOff, isResolving }: KeyPickupHandoffStatus =
    useKeyPickupHandoffStatus(originalEvent);

  if (!event || !originalEvent) {
    return null;
  }

  const getEventTypeString = (): string =>
    resolveEventTypeLabel(originalEvent.juniperEventType);

  const getEventType = (): EventType =>
    resolveEventTypeStyleKey(originalEvent.juniperEventType);

  const getEventUrl = (): string => {
    const baseUrl: string = window.location.origin;

    if (
      originalEvent.juniperEventType === WALKTHROUGH_EVENT_TYPE &&
      originalEvent.walkthroughId &&
      originalEvent.suiteId
    ) {
      return `${baseUrl}/app/suites/${originalEvent.suiteId}&${originalEvent.walkthroughId}`;
    }

    if (
      originalEvent.juniperEventType === SERVICE_REQUEST_EVENT_TYPE &&
      originalEvent.serviceRequestId
    ) {
      // Keep existing service request URL format unchanged.
      return `${baseUrl}/app/service-requests/${originalEvent.serviceRequestId}`;
    }

    return '#';
  };

  const getViewButtonLabel = (): string => {
    if (originalEvent.juniperEventType === WALKTHROUGH_EVENT_TYPE) {
      return 'View Walkthrough';
    }

    if (originalEvent.juniperEventType === SERVICE_REQUEST_EVENT_TYPE) {
      return 'View Service Request';
    }

    return 'View Event';
  };

  const hasValidUrl = (): string | boolean => {
    const url: string = getEventUrl();

    if (url === '#') {
      return false;
    }

    // For walkthroughs, ensure both the suiteId and walkthroughId are present.
    if (originalEvent.juniperEventType === WALKTHROUGH_EVENT_TYPE) {
      return originalEvent.suiteId && originalEvent.walkthroughId;
    }

    // For service requests, ensure serviceRequestId is present
    if (originalEvent.juniperEventType === SERVICE_REQUEST_EVENT_TYPE) {
      return originalEvent.serviceRequestId;
    }

    return false;
  };

  return (
    <GenericDialogue
      isOpen={isOpen}
      onClose={onClose}
      title={originalEvent.name || originalEvent.description || 'Event Details'}
      description={`Details for this ${getEventTypeString().toLowerCase()}`}
    >
      <div className="event-details">
        <EventDetailsHeader
          originalEvent={originalEvent}
          eventTypeString={getEventTypeString()}
          eventType={getEventType()}
          isCalcom={event.isCalcom}
        />
        <KeyPickupEventInformation originalEvent={originalEvent} />
        {originalEvent.juniperEventType === KEY_PICKUP_EVENT_TYPE &&
          (isResolving ? (
            <Skeleton variant="rect" height="8rem" />
          ) : isHandedOff ? (
            <KeyPickupHandedOffCard />
          ) : (
            <KeyPickupDetails originalEvent={originalEvent} />
          ))}
        {event.isCalcom && <CalcomEventDetailsSection event={originalEvent} />}
        {/* Show service request details for service requests. */}
        {originalEvent.juniperEventType === SERVICE_REQUEST_EVENT_TYPE && (
          <>
            <ServiceRequestDetails
              originalEvent={originalEvent}
              hasValidUrl={hasValidUrl}
              getEventUrl={getEventUrl}
              getViewButtonLabel={getViewButtonLabel}
            />
            <ServiceRequestSchedulingNote />
          </>
        )}
        {/* Show mobile CTA for walkthroughs. */}
        {originalEvent.juniperEventType === WALKTHROUGH_EVENT_TYPE && (
          <MobileAppCtaCard
            header="Complete walkthroughs on mobile"
            description="Prefer your phone? Walkthroughs can also be completed on the go in the Juniper mobile app."
          />
        )}
        <EventCreatedAt originalEvent={originalEvent} />
      </div>
    </GenericDialogue>
  );
};

export default EventDetailsDialog;
